import { FC } from 'react';
import CustomTable from '../../../shared/CustomTable/CustomTable';
import DistributionBars from '../../../shared/charts/DistributionBars';
import { DistributionSlice } from '../../../shared/charts/chart.types';
import Typhography from '../../../shared/Typhography/Typhography';
import { formatMinutes } from './analysis.constants';
import { IReportRow } from './useAnalysisFilter';
import styles from './CategoryReport.module.scss';

interface ICategoryReportProps {
  rows: IReportRow[];
  chartSlices: DistributionSlice[];
}

const HEADERS = ['Categoria', 'Durata totale', 'Media/seduta', 'Sedute'] as const;

/**
 * Report della selezione di filtro (feature #14): per ogni categoria/
 * sottocategoria scelta, durata totale, minuti medi per seduta e numero di
 * sedute («quante volte usata nel periodo»), più un mini grafico a barre.
 * I minuti di un blocco multi-categoria contano interi su ogni categoria
 * (decisione 2026-08-18): le durate per riga non si sommano al totale periodo.
 */
const CategoryReport: FC<ICategoryReportProps> = ({ rows, chartSlices }) => {
  const tableRows = rows.map((r) => ({
    _id: r.slug,
    label: r.label,
    color: r.color,
    isSub: r.isSub,
    durataLabel: formatMinutes(r.durata),
    mediaLabel: formatMinutes(r.media),
    sedute: r.sedute,
  }));

  return (
    <div className={styles.card}>
      <Typhography type="h3" size="bold-14" className={styles.title}>
        Report categorie selezionate
      </Typhography>

      <DistributionBars data={chartSlices} valueFormatter={formatMinutes} labelMode="value-percent" />

      <CustomTable
        headers={[...HEADERS]}
        data={tableRows}
        fields={{
          Categoria: {
            key: 'label',
            customComponent: (_value, record) => (
              <span className={record.isSub ? styles.voceSub : styles.voce}>
                <span className={styles.dot} style={{ background: record.color }} />
                {record.label}
              </span>
            ),
          },
          'Durata totale': { key: 'durataLabel', align: 'end' },
          'Media/seduta': { key: 'mediaLabel', align: 'end' },
          Sedute: { key: 'sedute', align: 'end' },
        }}
      />

      <Typhography size="regular-10" className={styles.note}>
        «Sedute» = giorni distinti in cui la categoria compare. I minuti di un
        blocco che sta in più categorie contano interi su ognuna, quindi la
        somma delle righe supera il tempo totale del periodo.
      </Typhography>
    </div>
  );
};

export default CategoryReport;
